/**
 * Infinite scroll
 *
 * @copyright 2018 Linguistica 360
 */

import { Script, injector } from '../shared/injector.js';

import $ from 'jquery';
import { $wrap } from '../shared/utils.js';
import debounce from 'lodash/debounce.js';
import stringHash from 'string-hash';
import throttle from 'lodash/throttle.js';


const CACHE_EXPIRE = 16; // number of pages navigated to outside the episode


class InfiniteCache extends Script {
    constructor() {
        super();

        this.expire();

        $('[data-infinite-cache-keep]').each($wrap(($keep) => {
            const [name, keep] = $keep.data('infinite-cache-keep').split(':');
            this.keep(name, keep);
        }));

        $('body').on('click', '[data-infinite-discard]', () => {
            Object.keys(sessionStorage).forEach((key) => {
                if (key.includes('-kex:') || key.includes('-kid:')) {
                    sessionStorage.removeItem(key);
                }
            });
        });
    }

    save(name, id, object) {
        const data = JSON.stringify(object);
        const hash = stringHash(data);

        sessionStorage.setItem(this.icKid(name, id), hash);
        sessionStorage.setItem(this.icKex(name, id), 1);

        try {
            sessionStorage.setItem(this.icSid(name, hash), data);
            sessionStorage.setItem(this.icSex(name, hash), CACHE_EXPIRE);
        } catch {
            console.warn('session storage is full');
        }
    }

    saveLocal(name, sids) {
        const data = sids.join(',');
        localStorage.setItem(`infinite-cache-expand-${name}`, data);
    }

    loadLocal(name) {
        return localStorage.getItem(`infinite-cache-expand-${name}`);
    }

    has(id) {
        return sessionStorage.getItem(id) !== null;
    }

    load(name) {
        const keys = Object.keys(sessionStorage).filter((key) => {
            const starts = key.startsWith(`infinite-cache-kex:${name}:`);
            return starts && sessionStorage.getItem(key) === '0';
        });

        if (keys.length === 0) return null;

        const kid = keys[0].replace('-kex:', '-kid:');
        if (!this.has(kid)) return null;

        const sid = this.icSid(name, sessionStorage.getItem(kid));
        if (!this.has(sid)) return null;

        return JSON.parse(sessionStorage.getItem(sid));
    }

    expire() {
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('infinite-cache-kex:')) {
                if (sessionStorage.getItem(key) == 0) {
                    sessionStorage.removeItem(key);
                    sessionStorage.removeItem(key.replace('-kex:', '-kid:'));
                }
                else sessionStorage.setItem(key, 0);
            }

            else if (key.startsWith('infinite-cache-sex:')) {
                if (sessionStorage.getItem(key) == 0) {
                    sessionStorage.removeItem(key);
                    sessionStorage.removeItem(key.replace('-sex:', '-sid:'));
                }
                else {
                    const newValue = sessionStorage.getItem(key) - 1;
                    sessionStorage.setItem(key, newValue);
                }
            }
        });
    }

    keep(name, key) {
        const kex = this.icKex(name, key);
        if (!this.has(kex)) return;

        sessionStorage.setItem(kex, 1);

        const kid = this.icKid(name, key);
        const sex = this.icSex(name, sessionStorage.getItem(kid));
        if (this.has(sex)) {
            sessionStorage.setItem(sex, CACHE_EXPIRE);
            const state = {
                infiniteCache: {
                    key,
                    name,
                    hash: sessionStorage.getItem(kid),
                },
            };
            // no need to preserve values in infinite since it will only
            // be one at a time anyway
            this.setState(state, '', true);
        }
    }

    icSid(name, id) {
        // state id
        return `infinite-cache-sid:${name}:${id}`;
    }

    icSex(name, id) {
        // state expiration
        return `infinite-cache-sex:${name}:${id}`;
    }

    icKid(name, id) {
        // keep id
        return `infinite-cache-kid:${name}:${id}`;
    }

    icKex(name, id) {
        // keep exiration
        return `infinite-cache-kex:${name}:${id}`;
    }

    onState(state) {
        if (state.infiniteCache) {
            const key = state.infiniteCache.key;
            const name = state.infiniteCache.name;
            const hash = state.infiniteCache.hash;

            const kid = this.icKid(name, key);
            const kex = this.icKex(name, key);

            sessionStorage.setItem(kid, hash);
            sessionStorage.setItem(kex, 1);
            this.keep(name, key); // update cache expiration
        }
    }
}

class Infinite extends Script {
    constructor($this, cache) {
        super();

        this.$this = $this;
        this.cache = cache;

        this.$main = $this.wrapInner('<div>').children().first();
        this.$search = $('<div>').hide().appendTo($this);

        if ($this.data('search')) {
            const $input = $(`${$this.data('search')} input`);
            const $button = $(`${$this.data('search')} button`);

            $input.on('input paste keyup', debounce(() => {
                this.search($input.val().trim());
            }, 500));

            $button.on('click', () => this.search($input.val().trim(), true));
        }

        this.url = $this.data('url');
        this.limit = $this.data('limit');

        this.$container = this.$main;

        $this.on('click', '[data-children]', this.$wrap(this.expand));
        $this.on('click', '[data-url]', $wrap(($this) => {
            this.saveState($this.closest('[data-sid]').data('sid'));
            location.href = $this.data('url');
        }));

        if ($this.data('cache-selector')) {
            if ($this.data('infinite')) {
                $this.on('click', $this.data('cache-selector'), $wrap(($this) => {
                    this.saveState($this.closest('[data-sid]').data('sid'));
                }));
            }
            else console.error('Infinite cache requires a name');

            const load = this.cache.load(this.$this.data('infinite'));
            if (load) {
                if (load.search) {
                    const $input = $(`${$this.data('search')} input`);
                    $input.val(load.search);

                    this.$main.hide();
                    this.$search.empty().data({
                        loading: false,
                        finished: false,
                        search: load.search,
                    }).show();
                    this.$container = this.$search;
                }

                this.$container.html(load.container);
                this.$container.find('[data-sid].active').removeClass('active');
                const $active = this.$container.find(`[data-sid=${load.sid}]`);
                if ($active.length) {
                    $active.addClass('active').get(0).scrollIntoView();
                }

                // fix player
                this.$container.find('.player-pause').addClass('player-play')
                    .removeClass('player-pause');
                this.$container.find('.player-playing').removeClass('playing');

                this.saveExpand();
            }
            else {
                const data = this.cache.loadLocal(this.$this.data('infinite'));
                if (data) {
                    data.split(',').forEach((sid) => {
                        $(`article[data-sid=${sid}] .expand-list`).trigger('click');
                    });
                }
            }
        }
    }

    getBottom() {
        return this.$this.offset().top + this.$this.height();
    }

    search(value, force = false) {
        if (value) {
            this.$main.hide();
            this.$search.empty().data({
                loading: false,
                finished: false,
                search: value,
            });
            this.$container = this.$search;
            this.query(force);
        }
        else {
            this.$search.empty().hide();
            this.$container = this.$main;
        }

        this.$container.show();
    }

    query() {
        const $container = this.$container; // capture current container for cb

        if ($container.data('loading') || $container.data('finished')) return;

        const offset = $container.children().length;

        const data = { offset, limit: this.limit };
        if ($container.data('search')) data.search = $container.data('search');

        $container.find('.loading').remove(); // in case of an old loading
        $container.append('<div class="loading">');
        $container.data('loading', true);

        $.get(this.url, data).then((res) => {
            if (res.trim()) $container.append(res);
            else $container.data('finished', true);
            $(window).trigger('scroll'); // load more if space left
        }).always(() => {
            $container.data('loading', false);
            $container.find('.loading').remove();
        });
    }

    expand($item) {
        const $button = $item.find('button.expand-list');

        if ($item.data('finished')) {
            $button.html($button.html() === '+' ? '-' : '+');
            $item.next().toggle();
            this.saveExpand();
            return;
        }

        const $container = $('<div class="inner">').insertAfter($item);
        $container.append('<div class="loading">');

        const data = { parent: $item.data('sid') };
        $.get(this.url, data).then((res) => {
            if (res.trim()) {
                $item.data('finished', true);
                $button.html('-');

                $container.html(res);
            }
            else $container.remove();
        }).fail(() => $container.remove());
    }

    saveState(sid) {
        if (!sid) return;

        this.cache.save(this.$this.data('infinite'), sid, {
            sid,
            search: this.$container.data('search'),
            container: this.$container.html().trim(),
        });
    }

    saveExpand() {
        const sids = $('.inner:visible').map(function () {
            return $(this).prev().data('sid');
        }).get();

        this.cache.saveLocal(this.$this.data('infinite'), sids);
    }
}

injector.injectClass(class extends Script {
    onLoad() {
        this.instances = [];
        this.cache = new InfiniteCache();

        $('[data-infinite]').each(this.$wrap(this.initInfinite));

        const $window = $(window);
        $window.on('scroll', throttle(() => {
            const off = $window.height() / 10;
            const bottom = $window.scrollTop() + $window.height() + off;

            this.instances.filter((instance) => {
                return instance.getBottom() < bottom;
            }).forEach((instance) => instance.query());
        }, 250)).trigger('scroll');
    }

    initInfinite($object) {
        this.instances.push(new Infinite($object, this.cache));
    }

    onState(state) {
        this.cache.onState(state);
    }
});
